import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Paper,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { Visibility, Delete, Description } from '@mui/icons-material';
import { SalesEvaluation, EvaluationStatus } from '../types/sales';

interface EvaluationsBoardProps {
  evaluations: SalesEvaluation[];
  onStatusChange: (evaluationId: string, newStatus: EvaluationStatus) => Promise<void>;
  onDelete: (evaluationId: string) => void;
  onGenerateProposal: (evaluation: SalesEvaluation) => void;
}

const COLUMNS: { id: EvaluationStatus; title: string; color: string }[] = [
  { id: 'DRAFT', title: 'Borrador', color: '#607d8b' },
  { id: 'LEAD', title: 'Lead', color: '#9e9e9e' },
  { id: 'PROSPECT', title: 'Prospecto', color: '#2196f3' },
  { id: 'OPPORTUNITY', title: 'Oportunidad', color: '#ff9800' },
  { id: 'NEGOTIATING', title: 'Negociación', color: '#9c27b0' },
  { id: 'CLOSED', title: 'Cerrado', color: '#4caf50' },
  { id: 'LOST', title: 'Perdido', color: '#f44336' },
];

const EvaluationsBoard: React.FC<EvaluationsBoardProps> = ({
  evaluations,
  onStatusChange,
  onDelete,
  onGenerateProposal,
}) => {
  const handleDragEnd = async (result: DropResult) => {
    const { destination, draggableId } = result;
    if (!destination) return;

    const sourceColumn = result.source.droppableId as EvaluationStatus;
    const destColumn = destination.droppableId as EvaluationStatus;
    if (sourceColumn === destColumn) return;

    await onStatusChange(draggableId, destColumn);
  };

  const evaluationsByStatus = COLUMNS.reduce((acc, column) => {
    acc[column.id] = evaluations.filter((e) => e.status === column.id);
    return acc;
  }, {} as Record<EvaluationStatus, SalesEvaluation[]>);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
        {COLUMNS.map((column) => (
          <Paper
            key={column.id}
            sx={{ minWidth: 280, maxWidth: 280, bgcolor: '#f5f5f5', borderRadius: 2 }}
          >
            {/* Column Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: column.color,
                color: 'white',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {column.title}
              </Typography>
              <Chip
                label={evaluationsByStatus[column.id].length}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 'bold' }}
              />
            </Box>

            {/* Droppable Column */}
            <Droppable droppableId={column.id}>
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    p: 1,
                    minHeight: 400,
                    maxHeight: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                    bgcolor: snapshot.isDraggingOver ? '#e3f2fd' : 'transparent',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {evaluationsByStatus[column.id].map((evaluation, index) => (
                    <Draggable key={evaluation.id} draggableId={evaluation.id} index={index}>
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            mb: 1,
                            cursor: 'grab',
                            bgcolor: 'white',
                            boxShadow: snapshot.isDragging ? 4 : 1,
                            transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                            '&:hover': { boxShadow: 3 },
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {evaluation.evaluation_name}
                            </Typography>

                            {/* Creator */}
                            {evaluation.created_by_name && (
                              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#1976d2', fontWeight: 500 }}>
                                Vendedor: {evaluation.created_by_name}
                              </Typography>
                            )}

                            {/* Client/Prospect */}
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              {evaluation.client_id
                                ? `Cliente: ${evaluation.client_id}`
                                : `Prospecto: ${evaluation.prospect_name}`}
                            </Typography>

                            {/* Pricing */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                Mensual: ${parseFloat(evaluation.total_monthly_payment).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {evaluation.contract_months} meses
                              </Typography>
                            </Box>

                            {/* Status Change */}
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <Select
                                value={evaluation.status}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newStatus = e.target.value as EvaluationStatus;
                                  if (newStatus !== evaluation.status) {
                                    onStatusChange(evaluation.id, newStatus);
                                  }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{ fontSize: '0.8rem', height: 32 }}
                              >
                                {COLUMNS.map((col) => (
                                  <MenuItem key={col.id} value={col.id} sx={{ fontSize: '0.85rem' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: col.color }} />
                                      {col.title}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Visibility />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const token = localStorage.getItem('token');
                                  const base = `https://cdp-ventas-frontend-22439252995.us-central1.run.app/evaluations/${evaluation.id}`;
                                  // Token bridge: pass JWT via URL fragment (never sent to server).
                                  // ventas-frontend reads it on boot, persists to localStorage, strips the hash.
                                  const url = token ? `${base}#token=${encodeURIComponent(token)}` : base;
                                  window.open(url, '_blank');
                                }}
                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                              >
                                Ver
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<Description />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onGenerateProposal(evaluation);
                                }}
                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                              >
                                PDF
                              </Button>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(evaluation.id);
                                }}
                                sx={{ ml: 'auto' }}
                                disabled={evaluation.status === 'CLOSED'}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {evaluationsByStatus[column.id].length === 0 && (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        color: 'text.secondary',
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '2px dashed #e0e0e0',
                      }}
                    >
                      <Typography variant="body2">Sin evaluaciones</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Droppable>
          </Paper>
        ))}
      </Box>
    </DragDropContext>
  );
};

export default EvaluationsBoard;
